import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  MenuItem,
  Button,
  Stack,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  DataObject as DataObjectIcon,
} from '@mui/icons-material';
import {
  careReportsService,
  CareReport,
  CareReportFieldDef,
  CareReportGroupBy,
  CareReportRow,
} from '../services/careReportsService';

// FEAT-056 — Customizable Care Reports (MVP-18.34). Report builder for both
// careseekers and caregivers: pick fields from the role-aware catalog, choose a
// date range and optional grouping, generate, and render the resulting rows +
// summary. The generated report can be exported as CSV or JSON.

const GROUP_BY_OPTIONS: Array<{ value: CareReportGroupBy; label: string }> = [
  { value: 'none', label: 'No grouping' },
  { value: 'day', label: 'By day' },
  { value: 'week', label: 'By week' },
  { value: 'month', label: 'By month' },
  { value: 'billingClassification', label: 'By billing classification' },
];

const toDateInputValue = (date: Date): string => date.toISOString().slice(0, 10);

const isAdlSummary = (
  value: unknown
): value is { done: number; notDone: number; notApplicable: number; total: number } =>
  typeof value === 'object' &&
  value !== null &&
  'done' in value &&
  'total' in value;

/** Human-readable cell value for the on-screen table. */
const formatCellValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (isAdlSummary(value)) {
    return `${value.done}/${value.total} done · ${value.notDone} not done · ${value.notApplicable} N/A`;
  }
  if (key === 'visitStartAt' || key === 'visitEndAt') {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return String(value);
};

/** CSV-safe value (flattens objects). */
const toCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  const raw = isAdlSummary(value)
    ? `done:${value.done} notDone:${value.notDone} notApplicable:${value.notApplicable} total:${value.total}`
    : String(value);
  if (/[",\n]/.test(raw)) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
};

const triggerDownload = (filename: string, contents: string, mimeType: string): void => {
  const blob = new Blob([contents], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const buildCsv = (report: CareReport): string => {
  const headers = report.fields.map((field) => field.label);
  const keys = report.fields.map((field) => field.key);
  const headerLine = headers.map(toCsvValue).join(',');
  const lines = report.rows.map((row) =>
    keys.map((key) => toCsvValue(row[key])).join(',')
  );
  return [headerLine, ...lines].join('\n');
};

const CareReportsPage: React.FC = () => {
  const [catalog, setCatalog] = useState<CareReportFieldDef[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const today = useMemo(() => new Date(), []);
  const thirtyDaysAgo = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  }, []);

  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState<string>(toDateInputValue(thirtyDaysAgo));
  const [dateTo, setDateTo] = useState<string>(toDateInputValue(today));
  const [groupBy, setGroupBy] = useState<CareReportGroupBy>('none');

  const [report, setReport] = useState<CareReport | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const loadCatalog = useCallback(async () => {
    try {
      setCatalogLoading(true);
      setCatalogError(null);
      const result = await careReportsService.getReportFields();
      setCatalog(result.fields);
      // Default to a sensible starter selection so the builder is usable
      // immediately for both roles.
      const defaults = result.fields
        .map((field) => field.key)
        .filter((key) => ['visitDate', 'totalHours'].includes(key));
      setSelectedFields(new Set(defaults.length > 0 ? defaults : result.fields.slice(0, 2).map((f) => f.key)));
    } catch (error) {
      setCatalogError(
        error instanceof Error ? error.message : 'Unable to load report fields.'
      );
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  const toggleField = (key: string) => {
    setSelectedFields((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const canGenerate = selectedFields.size > 0 && !!dateFrom && !!dateTo && !generating;

  const handleGenerate = async () => {
    if (selectedFields.size === 0) {
      setGenerateError('Select at least one field to include in the report.');
      return;
    }
    try {
      setGenerating(true);
      setGenerateError(null);
      const result = await careReportsService.generateReport({
        fields: catalog.map((f) => f.key).filter((key) => selectedFields.has(key)),
        dateFrom,
        dateTo,
        groupBy,
      });
      setReport(result);
    } catch (error) {
      setGenerateError(
        error instanceof Error ? error.message : 'Unable to generate the report.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleExportCsv = () => {
    if (!report) {
      return;
    }
    triggerDownload(`care-report-${report.dateFrom.slice(0, 10)}_${report.dateTo.slice(0, 10)}.csv`, buildCsv(report), 'text/csv');
  };

  const handleExportJson = () => {
    if (!report) {
      return;
    }
    triggerDownload(
      `care-report-${report.dateFrom.slice(0, 10)}_${report.dateTo.slice(0, 10)}.json`,
      JSON.stringify(report, null, 2),
      'application/json'
    );
  };

  const renderRows = (rows: CareReportRow[], fields: CareReportFieldDef[]) =>
    rows.map((row, index) => (
      <TableRow key={index} data-testid="care-report-row">
        {fields.map((field) => (
          <TableCell key={field.key}>{formatCellValue(field.key, row[field.key])}</TableCell>
        ))}
      </TableRow>
    ));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="care-reports-page">
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <AssessmentIcon color="primary" />
        <Typography variant="h4" component="h1">
          Care Reports
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Build a custom report from your visit data. Pick the fields you want, choose a date
        range, generate, and export a shareable summary.
      </Typography>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }} data-testid="care-report-builder">
        <Typography variant="h6" gutterBottom>
          1. Choose fields
        </Typography>
        {catalogLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : catalogError ? (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="care-report-catalog-error">
            {catalogError}
          </Alert>
        ) : (
          <FormGroup row data-testid="care-report-field-list">
            {catalog.map((field) => (
              <FormControlLabel
                key={field.key}
                sx={{ minWidth: 220 }}
                control={
                  <Checkbox
                    checked={selectedFields.has(field.key)}
                    onChange={() => toggleField(field.key)}
                    inputProps={
                      { 'data-testid': `care-report-field-${field.key}` } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
                label={field.label}
              />
            ))}
          </FormGroup>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          2. Date range &amp; grouping
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="From"
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'care-report-date-from' }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="To"
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'care-report-date-to' }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Group by"
              value={groupBy}
              onChange={(event) => setGroupBy(event.target.value as CareReportGroupBy)}
              inputProps={{ 'data-testid': 'care-report-group-by' }}
            >
              {GROUP_BY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {generateError && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="care-report-generate-error">
            {generateError}
          </Alert>
        )}

        <Box mt={3}>
          <Button
            variant="contained"
            size="large"
            disabled={!canGenerate}
            onClick={handleGenerate}
            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AssessmentIcon />}
            data-testid="care-report-generate"
          >
            {generating ? 'Generating…' : 'Generate report'}
          </Button>
        </Box>
      </Paper>

      {report && (
        <Paper elevation={1} sx={{ p: 3 }} data-testid="care-report-result">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            mb={2}
          >
            <Box>
              <Typography variant="h6">Report</Typography>
              <Typography variant="body2" color="text.secondary">
                {report.dateFrom.slice(0, 10)} → {report.dateTo.slice(0, 10)} ·{' '}
                {report.visitCount} visit{report.visitCount === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCsv}
                disabled={report.rows.length === 0}
                data-testid="care-report-export-csv"
              >
                Export CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<DataObjectIcon />}
                onClick={handleExportJson}
                data-testid="care-report-export-json"
              >
                Export JSON
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2} data-testid="care-report-summary">
            <Chip label={`${report.summary.visitCount} visits`} color="primary" variant="outlined" />
            <Chip label={`${report.summary.totalHours} hrs`} variant="outlined" />
            <Chip label={`${report.summary.totalMileage} mi`} variant="outlined" />
            <Chip
              label={`ADL ${report.summary.adl.done}/${report.summary.adl.total} done`}
              variant="outlined"
            />
          </Stack>

          {report.rows.length === 0 ? (
            <Alert severity="info" data-testid="care-report-empty">
              No visits matched the selected date range.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small" data-testid="care-report-table">
                <TableHead>
                  <TableRow>
                    {report.fields.map((field) => (
                      <TableCell key={field.key}>{field.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.groups
                    ? report.groups.map((group) => (
                        <React.Fragment key={group.key}>
                          <TableRow>
                            <TableCell
                              colSpan={report.fields.length}
                              sx={{ fontWeight: 600, bgcolor: 'action.hover' }}
                            >
                              {group.label} — {group.visitCount} visit
                              {group.visitCount === 1 ? '' : 's'}, {group.totalHours} hrs
                            </TableCell>
                          </TableRow>
                          {renderRows(group.rows, report.fields)}
                        </React.Fragment>
                      ))
                    : renderRows(report.rows, report.fields)}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default CareReportsPage;
